import { Icon } from "@cadmakers-ui/icons";
import React, { Dispatch } from "react";
import { CalendarPickerInput } from "@cadmakers-ui/core";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { RouteComponentProps, withRouter } from "react-router";
import {
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Row
} from "reactstrap";
import { UserState } from "redux-oidc";
import { endpoints } from "../..";
import { Feedback, ResourceRequest } from "../../Models/Feedback";
import ImageDropzone from "../Common/ImageDropzone/ImageDropzone";
import FileUploadButton from "../../../Common/Components/FileUploadButton/FileUploadButton";
import { fileService, imageUploadService, fileUploadService } from "../../Services";
import ElementSpacer from "../../../Layout/Components/ElementSpacer";
import { FeedbackFormInput } from "../Common/FeedbackFormInput";
import { ImageGallery } from "../Common/ImageGallery";
import { FeedbackPriority } from "../../Constants/feedbackEnums";
import FeedbackPrioritySetterContainer from "../Common/FeedbackPrioritySetter/FeedbackPrioritySetterContainer";
import { Image } from "../../Models/Image";
import { ToggleSetting } from "../../../Layout";
import { FeedbackFile } from "../../Models/FeedbackFile";
import FeedbackFileManager from "../Common/FeedbackFileManager/FeedbackFileManager";
import FeedbackFileDisplay from "../Common/FeedbackFileDisplay/FeedbackFileDisplay";
import FeedbackFormTagsInput from "../Common/FeedbackFormTagsInput";
import FeedbackFormIsPrivateInput from "../Common/FeedbackFormIsPrivateInput";
import iconStyles from "../../../Common/Styles/Icons.module.scss";
import FeedbackFormResourceRequestCategoryInput from "../Common/FeedbackFormResourceRequestCategoryInput";
import { userService } from "../../../User";
import { IAppState } from "../../../Common";

type Props = {
  hidePriorityDropdown: boolean;
  hideCanShowCommentsDropdown: boolean;
  hideTitle: boolean;
  hideImages: boolean;
  hideFiles: boolean;
  readonly: boolean;
  feedback: Feedback;
  canSubmit?: boolean;
  onSubmit?: (feedback: Feedback) => void;
  onDiscard?: () => void;
};

type StateProps = {
  user: UserState;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  resourceRequest: ResourceRequest;
  images: Image[];
  files: FeedbackFile[];
  dueDateDatePickerActual: Date;
};

const mapStateToProps = (state: IAppState) => ({
  user: state.oidc
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class EditResourceRequestForm extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps,
  State
> {
  private imageUploadPendingCount: number;

  private fileUploadPendingCount: number;

  static defaultProps = {
    hideTitle: false,
    hideImages: false,
    hideFiles: false,
    readonly: false,
    hidePriorityDropdown: false,
    hideCanShowCommentsDropdown: false
  };

  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps) {
    super(props);

    this.state = {
      resourceRequest: this.props.feedback as ResourceRequest,
      images: this.props.feedback.images,
      files: this.props.feedback.files,
      dueDateDatePickerActual: new Date()
    };

    this.imageUploadPendingCount = 0;
    this.fileUploadPendingCount = 0;
    this.onPrioritySelected = this.onPrioritySelected.bind(this);
    this.toggleAreCommentsEnabled = this.toggleAreCommentsEnabled.bind(this);
  }

  clearForm = () => {
    this.setState(prevState => ({
      resourceRequest: {
        ...prevState.resourceRequest,
        title: "",
        description: "",
        categoryName: "",
        specSheetLink: "",
        isPrivate: false
      }
    }));
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;
    this.setState(prevState => ({
      resourceRequest: { ...prevState.resourceRequest, [name as any]: value }
    }));
  };

  onChangeTags = (tagIds: number[]) => {
    this.setState(prevState => ({
      resourceRequest: { ...prevState.resourceRequest, tagIds }
    }));
  };

  onChangeIsPrivate = (isPrivate: boolean) => {
    this.setState(prevState => ({
      resourceRequest: { ...prevState.resourceRequest, isPrivate }
    }));
  };

  onChangeCategory = (newCategory: string) => {
    this.setState(prevState => ({
      resourceRequest: { ...prevState.resourceRequest, categoryName: newCategory }
    }));
  };

  onImagesAdded = (images: Image[]) => {
    this.imageUploadPendingCount += images.length;

    images.forEach(async image => {
      await imageUploadService.uploadImage(
        endpoints.feedback.images.uploadTemp(this.props.feedback.code),
        image,
        newImage => {
          this.setState(prevState => ({
            images: prevState.images.concat(newImage)
          }));
        },
        hash => {
          return this.state.images.find(x => x.fullSizeHash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload image", "This image has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar("Failed to upload image", result.error.message, response.status);
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload image",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.imageUploadPendingCount--;
    });
  };

  onImageDelete = (image: Image) => {
    if (image.fullSizeObjectPath) {
      this.setState(prevState => ({
        images: prevState.images.filter(x => x.fullSizeHash !== image.fullSizeHash)
      }));
    }
  };

  onFilesAdded = (files: FeedbackFile[], isCadmakersOnly: boolean) => {
    this.fileUploadPendingCount += files.length;

    files.forEach(async file => {
      await fileUploadService.uploadFile(
        endpoints.feedback.files.uploadTemp(this.props.feedback.code, isCadmakersOnly),
        file,
        newFile => {
          this.setState(prevState => ({
            files: prevState.files.concat(newFile)
          }));
        },
        hash => {
          return this.state.files.find(x => x.hash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload file", "This file has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar(
            "Failed to upload file",
            result?.error?.message ?? "",
            response.status
          );
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload file",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.fileUploadPendingCount--;
    });
  };

  onFileDelete = (file: FeedbackFile) => {
    if (file.path) {
      this.setState(prevState => ({
        files: prevState.files.filter(x => x.hash !== file.hash)
      }));
    }
  };

  onChangeDate = (dueDate: Date) => {
    const dateString = dueDate.toISOString();
    this.setState(prevState => ({
      resourceRequest: { ...prevState.resourceRequest, dueDate: dateString }
    }));
  };

  onChangeDatepickerActual = (actual: Date) => {
    this.setState(prevState => ({
      dueDateDatePickerActual: actual
    }));
  };

  onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (this.imageUploadPendingCount > 0) {
      this.showErrorSnackbar(
        "Failed to update resource request",
        "Please wait until all images are done uploading"
      );
      return;
    }

    if (this.fileUploadPendingCount > 0) {
      this.showErrorSnackbar(
        "Failed to update resource request",
        "Please wait until all files are done uploading"
      );
      return;
    }

    this.setState(
      prevState => ({
        resourceRequest: {
          ...prevState.resourceRequest,
          images: prevState.images,
          files: prevState.files
        }
      }),
      () => !!this.props.onSubmit && this.props.onSubmit(this.state.resourceRequest)
    );
  };

  onImageUploadClick = (files: File[]) => {
    this.onImagesAdded(fileService.filestoImages(files));
  };

  onFileUploadClick = (files: File[]) => {
    this.onFilesAdded(fileService.filestoFeedbackFiles(files, false), false);
  };

  onCadmakersFileUploadClick = (files: File[]) => {
    this.onFilesAdded(fileService.filestoFeedbackFiles(files, true), true);
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    let label = `${reason}: ${errorDescription}`;

    if (errorCode) {
      label += `(Error Code ${errorCode})`;
    }

    this.props.showSnack("COMMENT_BOX", {
      label,
      timeout: 5000
    });
  };

  onPrioritySelected = (feedbackCode: string, priority: FeedbackPriority) => {
    this.setState(prevState => ({
      resourceRequest: { ...prevState.resourceRequest, priority }
    }));
  };

  getImageGallery = () => {
    if (!this.props.hideImages) {
      if (!this.props.readonly) {
        return (
          <ImageDropzone
            images={this.state.images}
            onImagesAdded={this.onImagesAdded}
            onImageDelete={this.onImageDelete}
          />
        );
      }

      return <ImageGallery images={this.state.images} />;
    }

    return null;
  };

  getFileManager = () => {
    if (!this.props.hideFiles) {
      if (!this.props.readonly) {
        return (
          <FeedbackFileManager
            files={this.state.files.filter(f => !f.isCadmakersOnly)}
            onFilesAdded={this.onFilesAdded}
            onFileDelete={this.onFileDelete}
            isCadmakersOnly={false}
          />
        );
      }

      return <FeedbackFileDisplay files={this.state.files.filter(f => !f.isCadmakersOnly)} />;
    }

    return null;
  };

  getCadmakersFileManager = () => {
    if (!this.props.hideFiles) {
      if (!this.props.readonly) {
        return (
          <FeedbackFileManager
            files={this.state.files.filter(f => f.isCadmakersOnly)}
            onFilesAdded={this.onFilesAdded}
            onFileDelete={this.onFileDelete}
            isCadmakersOnly
          />
        );
      }

      return <FeedbackFileDisplay files={this.state.files.filter(f => f.isCadmakersOnly)} />;
    }

    return null;
  };

  toggleAreCommentsEnabled = () => {
    this.setState(prevState => ({
      resourceRequest: {
        ...prevState.resourceRequest,
        areCommentsEnabled: !prevState.resourceRequest.areCommentsEnabled
      }
    }));
  };

  render() {
    const isCadmakers = userService.isUserCadmakers(this.props.user);

    return (
      <Container fluid>
        <Form onSubmit={this.onSubmit}>
          {!this.props.hideTitle ? (
            <FeedbackFormInput
              label=" Title (Brief Description)"
              type="text"
              name="title"
              autoComplete="off"
              required
              value={this.state.resourceRequest.title}
              onChange={e => this.onChange(e)}
              description="This is the displayed title for the resource request. Please make it concise."
              disabled={this.props.readonly}
            />
          ) : null}

          <Row>
            {!this.props.readonly && !this.props.hideCanShowCommentsDropdown && (
              <Col md={2}>
                <ToggleSetting
                  settingName="Comments"
                  state={this.state.resourceRequest.areCommentsEnabled}
                  onText="Enabled"
                  offText="Disabled"
                  labeled
                  onToggle={this.toggleAreCommentsEnabled}
                />
                <br />
              </Col>
            )}
            {!this.props.readonly &&
              !!this.props.feedback.permissions &&
              !this.props.hidePriorityDropdown && (
                <Col md={4}>
                  <FeedbackPrioritySetterContainer
                    feedbackCode={this.props.feedback.code}
                    existingPriority={this.props.feedback.priority}
                    includeLabel
                    onChangePriority={this.onPrioritySelected}
                    isReadonly={!this.props.feedback.permissions.canPrioritize}
                  />
                </Col>
              )}
          </Row>
          <Row>
            <Col lg={6}>
              <FeedbackFormTagsInput
                selectedTagIds={this.state.resourceRequest.tagIds}
                onChangeTags={this.onChangeTags}
              />
            </Col>
            <Col lg={6}>
              <FeedbackFormIsPrivateInput
                isChecked={this.state.resourceRequest.isPrivate}
                onChange={this.onChangeIsPrivate}
              />
            </Col>
          </Row>
          <FeedbackFormInput
            label="Description"
            rows={3}
            type="textarea"
            name="description"
            value={this.state.resourceRequest.description ?? ""}
            onChange={e => this.onChange(e)}
            description="Description of the resource. Any important considerations. Links to images, supplier catalog"
          />
          <Row>
            <Col lg={6}>
              <FeedbackFormResourceRequestCategoryInput
                selectedCategory={this.state.resourceRequest.categoryName ?? undefined}
                onChangeCategory={this.onChangeCategory}
              />
            </Col>
          </Row>
          <FormGroup>
            <Label for="link">Spec sheet link</Label>
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <span className="input-group-text">
                  <Icon
                    appName="GeneralLayout"
                    assetName="GENERAL_L_ChainLink"
                    className={iconStyles.smallIcon}
                  />
                </span>
              </InputGroupAddon>
              <Input
                type="text"
                name="specSheetLink"
                autoComplete="off"
                value={this.state.resourceRequest.specSheetLink ?? ""}
                onChange={e => this.onChange(e)}
              />
            </InputGroup>
            <FormText>If available, a link to a sample spec sheet</FormText>
          </FormGroup>

          <FormGroup>
            <Label for="dueDate">
              <ElementSpacer spacing=".25rem">Due Date</ElementSpacer>
            </Label>
            <div style={{ width: 135 }}>
              <CalendarPickerInput
                value={
                  {
                    selectedDate: this.state.resourceRequest.dueDate
                      ? new Date(this.state.resourceRequest.dueDate)
                      : undefined,
                    actual: this.state.dueDateDatePickerActual
                  } as any
                }
                onChange={this.onChangeDate}
                onChangeMonth={this.onChangeDatepickerActual}
                disabledDays={[
                  {
                    before: new Date()
                  }
                ]}
              />
            </div>
            <FormText>
              Due date for this feedback to be progressed to the next workflow status
            </FormText>
          </FormGroup>

          {this.getImageGallery()}

          {this.getFileManager()}
          <br />
          {isCadmakers && this.getCadmakersFileManager()}

          {!this.props.readonly ? (
            <>
              <hr />
              <Row>
                <Col xs="auto">
                  <ElementSpacer>
                    <Button disabled={!this.props.canSubmit}>Save</Button>
                    <Button onClick={this.props.onDiscard}>Discard</Button>
                    <Button onClick={this.clearForm}>Clear</Button>
                    <FileUploadButton onClick={this.onImageUploadClick}>
                      Upload Images
                    </FileUploadButton>
                    <FileUploadButton onClick={this.onFileUploadClick}>
                      Upload Files
                    </FileUploadButton>
                    {isCadmakers && (
                      <FileUploadButton onClick={this.onCadmakersFileUploadClick}>
                        Upload Cadmakers Internal Files
                      </FileUploadButton>
                    )}
                  </ElementSpacer>
                </Col>
              </Row>
            </>
          ) : null}
        </Form>
      </Container>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditResourceRequestForm));
